import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MergeType from "@mui/icons-material/MergeType";
import SearchIcon from "@mui/icons-material/Search";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SelectOrganizationApplication from "components/SelectOrganizationApplication";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SnackbarMessage from "components/SnackbarMessage";
import StatusChip from "components/StatusChip";
import { hasUserPermission } from "helpers/ClaimHelpers";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTrashablePromises from "hooks/useTrashablePromises";
import ImpersonateUserButton from "pages/UserListPage/ImpersonateUserButton";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import {
  AdminListRequestOfSearchOrganizationUsersFilter,
  CreateUserLoginRequest,
  OrganizationUserDetails,
  OrganizationUserListItem,
  SearchOrganizationUsersFilter,
  UserLoginUrlResponse,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import { ClaimPermission, ClaimResource, ParentClaim } from "types/claimTypes";
import {
  OrganizationType,
  Severity,
  SnackbarMessageProps,
} from "types/common-types";

type RouteParamsType = {
  idFrom: string;
  parentUserId: string;
};

const UserList = () => {
  const { formatMessage: t } = useIntl();
  const {
    usersListUrl,
    userCreateUrl,
    userDetailsUrl,
    userStartMergeUrl,
    userConfirmMergeUrl,
    userService,
    organizationUserService,
  } = useGlobalContext();
  const navigate = useNavigate();
  const params = useParams<RouteParamsType>();
  const { registerPromise } = useTrashablePromises();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("lastName");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState((s) => s.userFilterSearchTerm);
  const applicationId = persistanceStore.useState((s) => s.userFilterApplicationId);
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const { organizationId, organizationTypeId } = contextStore.useState();
  const queryClient = useQueryClient();

  const mergeMode = !!params.idFrom;
  const title = mergeMode ? "mergeUserToMergeDataTo" : "menubarUsers";
  useListTitle(title);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get("email");
    if (emailParam) {
      persistanceStore.update((s) => {
        s.userFilterSearchTerm = emailParam;
      });
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get("error");
    const message = params.get("message");
    if (error === "true" && message) {
      setSnackbarMessage(
        new SnackbarMessageProps(decodeURIComponent(message), Severity.Error)
      );
      // Remove error params from URL
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (mergeMode) {
      contextStore.update((s) => {
        s.organizationIdDisabled = true;
      });
    }
    return () => {
      contextStore.update((s) => {
        s.organizationIdDisabled = false;
      });
    };
  }, [mergeMode]);

  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const mergingUserQuery = useStrictQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", params.idFrom],
    () => organizationUserService.getOrganizationUserDetails(params.idFrom!),
    { enabled: mergeMode }
  );

  const mergingUser = mergingUserQuery.data;

  const { data, isFetching, isFetched } = useStrictQuery(
    [
      "searchOrganizationUsers",
      organizationId,
      debouncedSearchTerm,
      applicationId,
      page,
      rowsPerPage,
      order,
      orderBy,
      params.idFrom,
      mergingUser?.userAccountId,
    ],
    () =>
      organizationUserService.searchOrganizationUsers(
        AdminListRequestOfSearchOrganizationUsersFilter.fromJS({
          organizationId,
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: SearchOrganizationUsersFilter.fromJS({
            searchText: debouncedSearchTerm,
            applicationId: applicationId,
            excludedIds: mergeMode ? [params.idFrom] : undefined,
            excludedUserAccountId: mergeMode
              ? mergingUser?.userAccountId
              : undefined,
            mergeUserOrganizationId: mergeMode
              ? mergingUser?.organizationId
              : undefined,
          }),
        })
      ),
    {
      keepPreviousData: true,
      enabled: !mergeMode || (mergeMode && mergingUser !== undefined),
    }
  );

  useEffect(() => {
    if (
      mergeMode &&
      mergingUser &&
      (!organizationId || mergingUser.organizationId !== organizationId)
    ) {
      contextStore.update((s) => {
        s.organizationId = mergingUser.organizationId;
        s.organizationIdDisabled = true;
      });
    }
  }, [mergeMode, mergingUser, organizationId]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, organizationId, applicationId]);

  useEffect(() => {
    persistanceStore.update((s) => {
      s.userFilterApplicationId = undefined;
    });
  }, [organizationId]);

  const UserActions = ({ value }: { value: OrganizationUserListItem }) => {
    const handleLoginButtonClick = useCallback(
      async (e: any) => {
        e.stopPropagation();

        var userLoginRequestModel = CreateUserLoginRequest.fromJS({
          userId: value.id,
          organizationId: value.organizationId,
        });

        var loginUrlResponse: UserLoginUrlResponse = await registerPromise(
          userService.createUserLoginUrl(userLoginRequestModel)
        );
        var loginUrl = loginUrlResponse?.returnUrl ?? "";

        if (loginUrl) {
          navigator.clipboard.writeText(loginUrl);
          setSnackbarMessage(
            new SnackbarMessageProps(t({ id: "copySuccessMessage" }))
          );
        } else {
          setSnackbarMessage(
            new SnackbarMessageProps(
              t({ id: "copyFailedMessage" }),
              Severity.Error
            )
          );
        }
      },
      [value]
    );

    const handleMergeUserButtonClick = useCallback(
      async (e: any) => {
        e.stopPropagation();

        if (mergeMode) {
          navigate(userConfirmMergeUrl(params.idFrom, value.id));
        } else {
          persistanceStore.update((store) => {
            store.userFilterSearchTerm = "";
          });

          navigate(userStartMergeUrl(value.id));
        }
      },
      [value]
    );

    return (
      <Box
        textAlign="center"
        style={{ display: "flex", justifyContent: "center" }}
      >
        {!mergeMode && (
          <>
            <ImpersonateUserButton
              user={value}
              size="small"
              color="green"
            />

            <Tooltip title={t({ id: "createLoginMessage" })}>
              <IconButton size="small" onClick={handleLoginButtonClick}>
                <VpnKeyIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
        {(!mergeMode || (mergeMode && organizationId && isFetched)) && (
          <Tooltip title={t({ id: "manage" })}>
            <IconButton
              size="small"
              style={{ color: "darkblue" }}
              onClick={handleMergeUserButtonClick}
            >
              <MergeType />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "firstName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "firstNameLabel" }),
    },
    {
      accessor: "lastName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "lastNameLabel" }),
    },
    {
      accessor: "email",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "emailLabel" }),
    },
    {
      accessor: "organizationName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "organization" }),
    },
    {
      accessor: "userStatusTypeName",
      type: "node",
      label: t({ id: "statusLabel" }),
      View: StatusChip,
    },
    {
      accessor: "value",
      align: "center",
      width: 0,
      type: "node",
      View: UserActions,
      noSort: true,
      label: t({ id: "actions" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(userDetailsUrl(id));
  };

  const onSync = async (ids: string[]) => {
    try {
      await registerPromise(userService.syncUsers(ids));
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "userSyncSuccess" }))
      );
      queryClient.invalidateQueries("searchOrganizationUsers");
    } catch {
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "userSyncError" }), Severity.Error)
      );
    }
  };

  const deleteUsersAllowed = useMemo(
    () =>
      hasUserPermission(
        ParentClaim.Users,
        ClaimResource.Users,
        ClaimPermission.Delete
      ),
    []
  );

  const syncUsersAllowed = useMemo(
    () =>
      hasUserPermission(
        ParentClaim.Users,
        ClaimResource.Users,
        ClaimPermission.Sync
      ),
    []
  );

  const handleDelete = async () => {
    try {
      await registerPromise(userService.deleteUsers(selectedUsers));
      queryClient.invalidateQueries("searchOrganizationUsers");
      setSelectedUsers([]);
      setShowDeleteDialog(false);
    } catch {
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "userDeleteError" }), Severity.Error)
      );
    }
  };

  if (mergeMode && !mergingUser && !mergingUserQuery.isFetching) {
    return <Navigate to={usersListUrl()} replace />;
  }

  const users = data?.data ?? [];

  return (
    <Container>
      {!mergeMode && (
        <Box mb={3} textAlign="center">
          <Tooltip
            title={
              organizationTypeId === OrganizationType.Client
                ? t({ id: "clientUsersMessage" })
                : t({ id: "organizationUsersMessage" })
            }
          >
            <span>
              <Fab
                aria-label="add"
                color="primary"
                onClick={() => navigate(userCreateUrl())}
                disabled={organizationTypeId === OrganizationType.Client}
              >
                <AddIcon />
              </Fab>
            </span>
          </Tooltip>
        </Box>
      )}

      <Box mb={3}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{t({ id: "filtersLabel" })}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={searchTerm ?? ""}
                  placeholder={t({ id: "searchPlaceholder" })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            persistanceStore.update((s) => {
                              s.userFilterSearchTerm = "";
                            })
                          }
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => {
                    persistanceStore.update((s) => {
                      s.userFilterSearchTerm = e.target.value;
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SelectOrganizationApplication
                  fullWidth
                  variant="outlined"
                  label={t({ id: "applicationLabel" })}
                  organizationId={organizationId ?? ""}
                  value={applicationId}
                  onChange={(value) =>
                    persistanceStore.update((s) => {
                      s.userFilterApplicationId = value;
                    })
                  }
                  disabled={!organizationId}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: users,
          loading: isFetching || mergingUserQuery.isFetching,
          dense: true,
          title: t({ id: title }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          onDelete:
            mergeMode || !deleteUsersAllowed
              ? undefined
              : (ids) => {
                  setSelectedUsers(ids);
                  setShowDeleteDialog(true);
                },
          executeMultiple:
            mergeMode || !syncUsersAllowed
              ? undefined
              : {
                  dialogLabel: t({ id: "syncUsersConfirmation" }),
                  onExecuteMultiple: onSync,
                  buttonLabel: t({ id: "syncUsers" }),
                  icon: <CloudSyncIcon />,
                },
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => userDetailsUrl(id)),
          ],
        }}
      />

      <DeleteEntityDialog
        dialogTitleKey="deleteUsersTitle"
        contentKey="deleteUsersConfirmation"
        onCancel={() => {
          setShowDeleteDialog(false);
          setSelectedUsers([]);
        }}
        handleDelete={handleDelete}
        open={showDeleteDialog}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default UserList;
